import { useCallback, useState } from 'react';

import { type Channel } from 'src/data/channel';
import type { Callback, VoidFunction } from 'src/lib/types';
// @ts-expect-error - no TypeScript definitions yet
import { useAuthorizeIntegration } from 'src/services/useAuthorizeIntegration';

import { META_CONNECT_CHOICE, type MetaConnectChoice } from './lib';

interface Params {
  channel: Channel;
  onResolve: Callback<MetaConnectChoice | undefined>;
  onClose: VoidFunction;
}

interface Handlers {
  bannerVisible: boolean;
  businessPending: boolean;
  onPickPersonal: VoidFunction;
  onPickBusiness: VoidFunction;
}

export const useHandlers = ({ channel, onResolve, onClose }: Params): Handlers => {
  const authorizeIntegration = useAuthorizeIntegration();
  const [bannerVisible, setBannerVisible] = useState(false);
  const [businessPending, setBusinessPending] = useState(false);

  const onPickPersonal = useCallback(() => {
    onResolve(META_CONNECT_CHOICE.personal);
    onClose();
  }, [onResolve, onClose]);

  const onPickBusiness = useCallback(async () => {
    setBusinessPending(true);
    setBannerVisible(false);
    try {
      // The connect + token write happen inside authorizeIntegration itself. Skip its account
      // loading so the selection popup (opened after this resolves) fetches them and shows a loader
      // once the Facebook dialog closes, matching the personal path.
      await authorizeIntegration(channel, { skipAccountsLoading: true });
      onResolve(META_CONNECT_CHOICE.business);
      onClose();
    } catch {
      // A denial comes back as a rejection; a user who just closes the Meta popup instead
      // makes this await hang forever, which is why the personal option must stay clickable.
      setBannerVisible(true);
    } finally {
      setBusinessPending(false);
    }
  }, [authorizeIntegration, channel, onResolve, onClose]);

  return { bannerVisible, businessPending, onPickPersonal, onPickBusiness };
};
